import { Suspense, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { RouteFallback } from '../components/RouteFallback';

const NAV = [
  { to: '/app', icon: 'fa-solid fa-gauge-high', label: '대시보드', end: true },
  { to: '/app/leads', icon: 'fa-solid fa-bullhorn', label: '영업' },
  { to: '/app/quotes', icon: 'fa-solid fa-file-invoice-dollar', label: '견적서' },
  { to: '/app/projects', icon: 'fa-solid fa-helmet-safety', label: '공사관리' },
  { to: '/app/materials', icon: 'fa-solid fa-boxes-stacked', label: '자재' },
  { to: '/app/purchase-orders', icon: 'fa-solid fa-truck-fast', label: '발주' },
  { to: '/app/suppliers', icon: 'fa-solid fa-store', label: '공급사' },
  { to: '/app/subcontractors', icon: 'fa-solid fa-handshake', label: '하청업체' },
  { to: '/app/invoices', icon: 'fa-solid fa-receipt', label: '세금계산서' },
  { to: '/app/as-requests', icon: 'fa-solid fa-tools', label: 'A/S' },
  { to: '/app/admin', icon: 'fa-solid fa-shield-halved', label: '관리자', ownerOnly: true },
];

export function AppShell() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { mode, toggle } = useTheme();
  const location = useLocation();

  return (
    <div className="min-h-dvh">
      {/* 사이드바 (lg+ 고정) */}
      <aside
        aria-label="사이드 내비게이션"
        className={[
          'fixed top-0 bottom-0 left-0 z-40 w-64 lg:w-60 px-4 py-6',
          'bg-white/80 dark:bg-surface-950/80 backdrop-blur-xl border-r border-surface-200/60 dark:border-white/10',
          'transition-transform lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        <div className="flex items-center gap-2 font-display font-bold text-lg select-none">
          <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand-600 text-white">
            <i className="fa-solid fa-cube" aria-hidden="true" />
          </span>
          <span>빌드업</span>
        </div>
        {user?.tenantName && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-brand-600/10 border border-brand-600/20">
            <p className="text-[10px] uppercase tracking-wider text-surface-900/60 dark:text-surface-100/60">
              회사
            </p>
            <p className="text-sm font-semibold text-brand-700 dark:text-brand-400 truncate">
              {user.tenantName}
            </p>
          </div>
        )}
        <nav className="mt-8 space-y-1">
          {NAV.filter((n) => !n.ownerOnly || user?.role === 'owner').map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  // min-h-11 (44px) — DESIGN.md 8장 터치 타겟 기준
                  'flex items-center gap-3 rounded-xl px-3 py-2 min-h-11 text-sm transition',
                  isActive
                    ? 'bg-brand-600 text-white'
                    : 'hover:bg-white/50 dark:hover:bg-white/[0.04]',
                ].join(' ')
              }
            >
              <i className={n.icon} aria-hidden="true" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* 메인 영역 */}
      <div className="lg:pl-60 min-h-dvh flex flex-col">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 dark:bg-surface-950/70 border-b border-surface-200/60 dark:border-white/10">
          <div className="h-16 px-4 sm:px-6 flex items-center justify-between">
            <button
              type="button"
              className="lg:hidden h-11 w-11 rounded-xl border border-surface-200 dark:border-white/10"
              onClick={() => setOpen((v) => !v)}
              aria-label="메뉴 토글"
            >
              <i className={open ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'} aria-hidden="true" />
            </button>
            {/* 회사명 (테넌트) — 멀티테넌트 가시화 */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-600/10 text-brand-700 dark:text-brand-400 border border-brand-600/20 text-sm font-semibold max-w-[40vw]">
                <i className="fa-solid fa-building" aria-hidden="true" />
                <span className="truncate">{user?.tenantName ?? '—'}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggle}
                className="h-11 w-11 rounded-xl border border-surface-200 dark:border-white/10"
                aria-label="테마 전환"
              >
                <i className={mode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon'} aria-hidden="true" />
              </button>
              <span
                className="hidden sm:inline-flex items-center gap-2 text-sm"
                title={user?.role}
              >
                <i className="fa-solid fa-circle-user" aria-hidden="true" />
                <span className="font-medium">{user?.name ?? user?.role}</span>
                <span className="text-xs text-surface-900/60 dark:text-surface-100/60">
                  · {user?.role}
                </span>
              </span>
              <button
                type="button"
                className="btn btn-ghost min-h-11"
                onClick={() => void logout()}
              >
                로그아웃
              </button>
            </div>
          </div>
        </header>
        <main id="main" className="flex-1 px-4 sm:px-6 lg:px-8 py-8">
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
